import React, { Fragment, type FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import {
  Button,
  fontSize,
  Label,
  Modal,
  SimpleSelect,
  Text,
  TextButton,
  useBooleanState,
} from '@retailer-platform/shared-components'
import { css } from '@emotion/react'
import { useDomainMessage, useDomainMessages } from 'domains/collections/utils/domain/intl'
import { CodeLookupMatcher } from 'domains/collections/components/collections-form/components/code-lookup-matcher/CodeLookupMatcher'
import LoadingDots from 'legacy/components/LoadingDots'
import { useGetCodeLookupBatchLazyQuery } from 'domains/collections/api'
import { CollectionsRuleViewProducts } from 'domains/collections/components/collections-form/components/rules/CollectionsRuleViewProducts'
import { type CodeLookupsCodeType } from 'domains/collections/components/collections-form/components/code-lookup-matcher/utils/CodeLookupMatcher.types'
import { CollectionsFormField } from 'domains/collections/components/collections-form/components/form-field/CollectionsFormField'
import { type CollectionsRuleProps } from 'domains/collections/components/collections-form/components/rules/utils/CollectionsRules.types'
interface CollectionsRuleProductCodesFormComponentProps {
  onChange: (productIds: string[]) => void
  value: string[]
  retailerId: string
}

const viewProductsStyles = css({
  display: 'flex',
})

const CollectionsRuleProductCodesFormComponent: FunctionComponent<
  CollectionsRuleProductCodesFormComponentProps
> = ({ onChange, value, retailerId }) => {
  const [productCodesModalOpen, openProductCodesModal, closeProductCodesModal] =
    useBooleanState(false)
  const [codeLookupBatchId, setCodeLookupBatchId] = useState<string | undefined>()
  const [matchedProducts, setMatchedProducts] = useState<string[] | undefined>(value)
  const [viewProductsModalOpen, openViewProductsModal, closeViewProductsModal] =
    useBooleanState(false)

  const [codeType, setCodeType] = useState<CodeLookupsCodeType>('UPC')

  // TODO: change this to use the processed and unprocessed counts of the batch for a progress bar
  // will just use a loading gif for now
  const [matchingInProgress, setMatchingInProgress] = useState(false)
  const [getCodeLookupBatch, { data, stopPolling }] = useGetCodeLookupBatchLazyQuery({
    pollInterval: 2000,
  })

  useEffect(() => {
    // when codeLookupBatchId is set, start polling to check values
    // when it's done, stop polling, and make query to get all products
    if (data?.catalogCodeLookupBatch?.codeLookupProcessCompleted) {
      stopPolling()
      setMatchingInProgress(false)
      setMatchedProducts(data.catalogCodeLookupBatch.perfectMatchProductIds)
      onChange(data.catalogCodeLookupBatch.perfectMatchProductIds)
    }
  }, [data, stopPolling, onChange])

  const onCodeLookupBatchCreation = useCallback(
    (codeLookupBatchId: string) => {
      setCodeLookupBatchId(codeLookupBatchId)
      closeProductCodesModal()
      setMatchingInProgress(true)

      getCodeLookupBatch({
        variables: {
          id: codeLookupBatchId,
        },
      })
    },
    [setCodeLookupBatchId, closeProductCodesModal, getCodeLookupBatch]
  )

  const messages = useDomainMessages({
    upc: 'collectionsDomain.upc',
    rrc: 'collectionsDomain.rrc',
    cancel: 'collectionsDomain.cancel',
    enterUpc: 'collectionsDomain.collectionsForm.section.rules.productCodes.enterUpcs',
    enterRrc: 'collectionsDomain.collectionsForm.section.rules.productCodes.enterRrcs',
    enterUpcModalHeader:
      'collectionsDomain.collectionsForm.section.rules.productCodes.enterUpcs.header',
    enterRrcModalHeader:
      'collectionsDomain.collectionsForm.section.rules.productCodes.enterRrcs.header',
    viewProducts: 'collectionsDomain.collectionsForm.section.rules.productCodes.viewProducts',
    viewProductsHeader:
      'collectionsDomain.collectionsForm.section.rules.productCodes.viewProducts.header',
    codeTypeLabel: 'collectionsDomain.collectionsForm.section.rules.productCodes.codeTypeLabel',
  })

  const matchedCountMessage = useDomainMessage(
    'collectionsDomain.collectionsForm.section.rules.productCodes.viewProducts.matched',
    { count: matchedProducts?.length }
  )

  const codeTypeOptions: Array<{
    label: string
    value: CodeLookupsCodeType
    isDisabled?: boolean
  }> = [
    {
      label: messages.upc,
      value: 'UPC',
    },
    {
      label: messages.rrc,
      value: 'RRC',
      isDisabled: !retailerId,
    },
  ]

  return (
    <div>
      {!codeLookupBatchId && !matchedProducts && (
        <Fragment>
          <Label>{'Code Type'}</Label>
          <CollectionsFormField>
            <SimpleSelect
              onChange={codeType => setCodeType(codeType)}
              value={codeType}
              options={codeTypeOptions}
            />
          </CollectionsFormField>

          <Button onClick={openProductCodesModal}>
            {codeType === 'UPC' ? messages.enterUpc : messages.enterRrc}
          </Button>
        </Fragment>
      )}
      {matchingInProgress && <LoadingDots height={20} />}
      {matchedProducts && (
        <div css={viewProductsStyles}>
          <Text size="medium">{matchedCountMessage}</Text>
          <TextButton
            css={css({ fontSize: fontSize.X15 })}
            type="button"
            onClick={openViewProductsModal}
          >
            {messages.viewProducts}
          </TextButton>
        </div>
      )}
      <Modal
        isOpen={productCodesModalOpen}
        onRequestClose={closeProductCodesModal}
        headerContent={
          codeType === 'UPC' ? messages.enterUpcModalHeader : messages.enterRrcModalHeader
        }
        bodyContent={
          <CodeLookupMatcher
            onCancel={closeProductCodesModal}
            onCodeLookupBatchCreation={onCodeLookupBatchCreation}
            codeType={codeType}
            retailerId={retailerId}
          />
        }
      />
      <Modal
        maxWidth={800}
        isOpen={viewProductsModalOpen}
        onRequestClose={closeViewProductsModal}
        headerContent={messages.viewProductsHeader}
        bodyContent={<CollectionsRuleViewProducts productIds={matchedProducts || []} />}
      />
    </div>
  )
}

export const CollectionsRuleProductCodes: FunctionComponent<CollectionsRuleProps> = ({
  control,
  formPrefix,
  retailerId,
}) => (
  <Controller
    as={controllerProps => <CollectionsRuleProductCodesFormComponent {...controllerProps} />}
    control={control}
    name={`${formPrefix}.productIds`}
    retailerId={retailerId}
  />
)
